import { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowUpDown, Download } from 'lucide-react';
import { CampaignMetric } from '../../types';

interface CampaignTableProps {
  data: CampaignMetric[];
  loading: boolean;
  onExport: () => void;
}

type SortKey = 'campaign' | 'leads' | 'conversions' | 'conversionRate' | 'totalQuoteValue';
type SortDirection = 'asc' | 'desc';

export function CampaignTable({ data, loading, onExport }: CampaignTableProps) {
  const [sortKey, setSortKey] = useState<SortKey>('leads');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  // Defensive: ensure data is always an array
  const safeData = Array.isArray(data) ? data : [];

  const sortedData = useMemo(() => {
    return [...safeData].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number);
    });
  }, [safeData, sortKey, sortDirection]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const SortableHeader = ({ column, label }: { column: SortKey; label: string }) => (
    <TableHead
      className="cursor-pointer hover:bg-muted/50 transition-colors"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown className="h-4 w-4" />
      </div>
    </TableHead>
  );

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Performance por Campanha</CardTitle>
          <CardDescription>Métricas de cada campanha UTM</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center">
            <div className="animate-pulse text-muted-foreground">Carregando...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Performance por Campanha</CardTitle>
          <CardDescription>Métricas de cada campanha UTM</CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={onExport}>
          <Download className="h-4 w-4 mr-2" />
          Exportar CSV
        </Button>
      </CardHeader>
      <CardContent>
        {safeData.length === 0 ? (
          <div className="h-[200px] flex items-center justify-center text-muted-foreground">
            Nenhuma campanha encontrada no período
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableHeader column="campaign" label="Campanha" />
                  <TableHead>Fonte</TableHead>
                  <TableHead>Meio</TableHead>
                  <SortableHeader column="leads" label="Leads" />
                  <SortableHeader column="conversions" label="Conversões" />
                  <SortableHeader column="conversionRate" label="Taxa" />
                  <SortableHeader column="totalQuoteValue" label="Valor Total" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((campaign, index) => (
                  <TableRow key={`${campaign.campaign}-${index}`}>
                    <TableCell className="font-medium max-w-[200px] truncate" title={campaign.campaign}>
                      {campaign.campaign}
                    </TableCell>
                    <TableCell>{campaign.source}</TableCell>
                    <TableCell>{campaign.medium}</TableCell>
                    <TableCell>{campaign.leads.toLocaleString('pt-BR')}</TableCell>
                    <TableCell>{campaign.conversions.toLocaleString('pt-BR')}</TableCell>
                    <TableCell>
                      <span
                        className={
                          campaign.conversionRate >= 20
                            ? 'text-green-600 font-medium'
                            : campaign.conversionRate >= 10
                            ? 'text-amber-600'
                            : 'text-muted-foreground'
                        }
                      >
                        {campaign.conversionRate.toFixed(1)}%
                      </span>
                    </TableCell>
                    <TableCell>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL'
                      }).format(campaign.totalQuoteValue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
